import { getAmendmentItemsFromPacklists } from './helpers.utils'

/**
 * Normalises the /v3/packlists response into packlist groups, one per delivery order.
 *
 * A POA ("post-order amendment") order is a separate delivery order holding the items a
 * customer added after placing the original. LIT-540 makes the endpoint return both sides
 * of that relationship grouped by order; until it ships the response is still a flat list,
 * so this accepts either and is deliberately total — it never throws, whatever it is given.
 * A malformed payload degrades to "no groups" and leaves the page renderable, rather than
 * surfacing as a fetch error the way an unguarded `.map` would.
 *
 * Note `role`/`isOwn` come from the response rather than from the page's order object,
 * which is not schema-guaranteed: on row-click navigation it is the picking list row from
 * router state, on direct load it is an orderDetails row, and the two carry different fields.
 */

export const GROUP_ROLE = { MAIN: 'MAIN', AMENDED: 'AMENDED' }

const toArray = (value) => (Array.isArray(value) ? value : [])

const isPresent = (value) =>
  value !== undefined && value !== null && value !== ''

const normaliseRole = (role) =>
  String(role || '').toUpperCase() === GROUP_ROLE.AMENDED
    ? GROUP_ROLE.AMENDED
    : GROUP_ROLE.MAIN

// getAmendmentItemsFromPacklists dereferences packlist.items directly, so every packlist
// has to carry an items array before it runs.
const withAmendmentItems = (packlists) =>
  getAmendmentItemsFromPacklists(
    toArray(packlists)
      .filter((packlist) => packlist && typeof packlist === 'object')
      .map((packlist) => ({ ...packlist, items: toArray(packlist.items) }))
  )

const toGroup = (rawGroup) => {
  const packlists = withAmendmentItems(rawGroup?.packlists)
  return {
    deliveryOrderNo: isPresent(rawGroup?.deliveryOrderNo)
      ? String(rawGroup.deliveryOrderNo)
      : '',
    role: normaliseRole(rawGroup?.role),
    addedAt: rawGroup?.addedAt || '',
    status: rawGroup?.status || '',
    packlists,
    packlistCount: packlists.length,
    itemCount: packlists.reduce(
      (total, packlist) => total + packlist.items.length,
      0
    ),
  }
}

const readRawGroups = (response) => {
  if (Array.isArray(response?.packlistGroups)) {
    return response.packlistGroups
  }
  if (Array.isArray(response?.groups)) {
    return response.groups
  }
  return null
}

// A payload carrying neither a groups array nor a packlists array is not something this
// endpoint should ever return. Callers surface that as a fetch failure rather than as an
// empty order, which is what the page did before grouping existed.
const isRecognisedResponse = (response) =>
  readRawGroups(response) !== null || Array.isArray(response?.packlists)

/**
 * Which group belongs to the order the page was opened on. Ordered by how trustworthy the
 * signal is; the final fall back to MAIN keeps the existing table populated even when
 * nothing matches, so the page never goes blank on an unexpected payload.
 */
const resolveOwnIndex = (groups, { currentDeliveryOrderNo, orderId }) => {
  const byDeliveryOrderNo = groups.findIndex(
    (group) =>
      isPresent(currentDeliveryOrderNo) &&
      group.deliveryOrderNo === String(currentDeliveryOrderNo)
  )
  if (byDeliveryOrderNo !== -1) {
    return byDeliveryOrderNo
  }

  const byOrderId = groups.findIndex(
    (group) =>
      isPresent(orderId) &&
      group.packlists.some(
        (packlist) => String(packlist.orderId) === String(orderId)
      )
  )
  if (byOrderId !== -1) {
    return byOrderId
  }

  if (groups.length === 1) {
    return 0
  }

  return groups.findIndex((group) => group.role === GROUP_ROLE.MAIN)
}

// addedAt is the amendment's created_at: "YYYY-MM-DD HH:mm:ss" from picking-service, or ISO.
// Unparseable or missing values sort last.
const addedAtSortKey = (addedAt) => {
  const time = Date.parse(String(addedAt || '').replace(' ', 'T'))
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time
}

const byCreationOrder = (a, b) => {
  const diff = addedAtSortKey(a.addedAt) - addedAtSortKey(b.addedAt)
  if (diff !== 0 && !Number.isNaN(diff)) {
    return diff
  }
  return (Number(a.deliveryOrderNo) || 0) - (Number(b.deliveryOrderNo) || 0)
}

/**
 * Numbers amendment groups by when they were created (Amendment 1, 2, …) and orders them
 * oldest first. Only the amendment slots are reshuffled, so every other group keeps its
 * position. Non-amendment groups get amendmentNumber null.
 */
const numberAmendments = (groups) => {
  const amended = groups
    .filter((group) => group.role === GROUP_ROLE.AMENDED)
    .sort(byCreationOrder)
  let next = 0

  return groups.map((group) => {
    if (group.role !== GROUP_ROLE.AMENDED) {
      return { ...group, amendmentNumber: null }
    }
    const ordered = amended[next]
    next += 1
    return { ...ordered, amendmentNumber: next }
  })
}

export const normalisePacklistGroups = (
  response,
  { currentDeliveryOrderNo, orderId } = {}
) => {
  const rawGroups = readRawGroups(response)
  const isGrouped = rawGroups !== null

  const parsed = isGrouped
    ? numberAmendments(rawGroups.map(toGroup))
    : [
        toGroup({
          packlists: response?.packlists,
          role: GROUP_ROLE.MAIN,
          deliveryOrderNo: currentDeliveryOrderNo,
        }),
      ]

  const ownIndex = resolveOwnIndex(parsed, { currentDeliveryOrderNo, orderId })
  const groups = parsed.map((group, index) => ({
    ...group,
    isOwn: index === ownIndex,
  }))
  const ownGroup = ownIndex === -1 ? null : groups[ownIndex]

  return {
    groups,
    isGrouped,
    isRecognised: isRecognisedResponse(response),
    ownPacklists: ownGroup ? ownGroup.packlists : [],
    otherGroups: groups.filter((group) => !group.isOwn),
  }
}

export default normalisePacklistGroups
